<template>
  <div
    class="your-stuff-page-header-employee-only"
    data-name="YourStuffPageHeaderEmployeeOnly"
  >
    <div class="your-stuff-page-header-employee-only__title-row">
      <div
        class="your-stuff-page-header-employee-only__title"
        :class="{
          'your-stuff-page-header-employee-only__title--fill': !accountUser,
        }"
      >
        Your Stuff
      </div>
      <div
        v-if="accountUser"
        class="account-area"
        data-testid="employee-only-account-area"
      >
        <div class="account-area__identity-group">
          <ExAvatar
            class="account-area__avatar"
            size="small"
            :picture="accountUser.avatar"
            :firstName="accountUser.firstName"
            :lastName="accountUser.lastName"
          />
          <div class="account-area__identity">
            <span class="account-area__name">
              {{ accountUser.firstName }} {{ accountUser.lastName }}
            </span>
            <span class="account-area__email">{{ accountUser.email }}</span>
          </div>
        </div>
        <EXTooltip title="Sign Out" placement="bottom" :delay="200">
          <button
            type="button"
            class="account-area__logout"
            data-testid="employee-only-logout"
            aria-label="Log out"
            @click="emit('logoutClicked')"
          >
            <img
              class="account-area__logout-icon"
              :src="logoutIcon"
              width="17"
              height="17"
              alt=""
            />
          </button>
        </EXTooltip>
      </div>
      <div class="brand-area">
        <img
          class="brand-area__logo"
          data-testid="employee-only-company-logo"
          :src="companyLogo"
          width="189"
          height="38"
          alt="exIQtive"
        />
        <AccountNameChip
          v-if="companyName"
          :name="companyName"
          data-testid="employee-only-company-chip"
        />
      </div>
      <HelpMenu />
    </div>
    <HorizontalMenu
      :items="menuItems"
      size="small"
      :bottomLine="false"
      :fixedTabs="false"
      :value="selectedTab"
      :hideLines="true"
      @onChange="handleTabChange"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import AccountNameChip from "@components/AccountNameChip/AccountNameChip.vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import HelpMenu from "@components/HelpMenu/HelpMenu.vue";

const companyLogo = require("@components/assets/your-stuff-page-header/exiqtive-wordmark.png");
const logoutIcon = require("@components/assets/your-stuff-page-header/log-out-01.svg");

const props = defineProps({
  /** Current tab's canonical route path (e.g. "/your-stuff/employee"), not a numeric index. */
  value: {
    type: String,
    default: "",
  },
  coachInspectCount: {
    type: Number,
    default: null,
  },
  masterBadgeCount: {
    type: Number,
    default: null,
  },
  pendingTasksCount: {
    type: Number,
    default: null,
  },
  activeProceduresCount: {
    type: Number,
    default: null,
  },
  /** ER-11462 — Rule 1: hide "As a Coach" for an Access that is not a qualifying coach. */
  showCoachTab: {
    type: Boolean,
    default: true,
  },
  /** ER-11463 — Rule 1: hide "As an Architect" for an Access with no current MASTER badge. */
  showMasterTab: {
    type: Boolean,
    default: true,
  },
  /** Visible when Settings > Directories > Show Employees/Responsibilities Directory is ON. */
  showDirectoriesTab: {
    type: Boolean,
    default: false,
  },
  /** ER-8051 — Procedures module is not active yet; stays hidden until it ships. */
  showProceduresTab: {
    type: Boolean,
    default: false,
  },
  /** idea-MDP-575 — Your Tasks; must match YourStuffPage.tabRoutes. */
  showTasksTab: {
    type: Boolean,
    default: false,
  },
  /** { firstName, lastName, email, avatar } — sourced from the current Access. */
  accountUser: {
    type: Object,
    default: null,
  },
  /** Company (account) name shown in the top-right purple chip (AccountNameChip). */
  companyName: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["tabSelected", "logoutClicked"]);

/**
 * Fixed employee-only tab set. Each navigable tab carries the canonical route it
 * resolves to in YourStuffPage.tabRoutes, so the parent can navigate by route rather
 * than by a raw index (which drifted when Onboarding was prepended — ER-11976).
 *
 * As a Coach / As an Architect / Procedures / Directories are hidden entirely — not just
 * counter-less — per the Menu Items visibility rules (no admin override, no fallback).
 * Count badges are only shown for tabs whose count prop is provided (non-null).
 */
const menuItems = computed(() => {
  const items = [
    { title: "As Employee", count: null, route: "/your-stuff/employee" },
  ];
  if (props.showCoachTab) {
    items.push({ title: "As a Coach", count: props.coachInspectCount, route: "/your-stuff/coach" });
  }
  if (props.showMasterTab) {
    items.push({ title: "As an Architect", count: props.masterBadgeCount, route: "/your-stuff/as-architect" });
  }
  if (props.showTasksTab) {
    items.push({ title: "Your Tasks", count: props.pendingTasksCount, route: "/your-stuff/tasks" });
  }
  if (props.showProceduresTab) {
    items.push({ title: "Procedures", count: props.activeProceduresCount, route: "/your-stuff/procedures" });
  }
  if (props.showDirectoriesTab) {
    items.push({ title: "Directories", count: null, route: "/your-stuff/directories" });
  }
  items.push({ title: "Your Profile", count: null, route: "/your-stuff/profile" });
  return items;
});

/** Map the current route path (props.value) to a menuItems index for the highlight. */
const selectedTab = computed(() => {
  const route = (props.value || "").replace(/\/$/, "") || "/";
  let bestIdx = 0;
  let bestLen = -1;
  menuItems.value.forEach((item, index) => {
    if (!item.route) return;
    const itemRoute = (item.route || "").replace(/\/$/, "") || "/";
    if (route === itemRoute || route.startsWith(`${itemRoute}/`)) {
      if (itemRoute.length > bestLen) {
        bestLen = itemRoute.length;
        bestIdx = index;
      }
    }
  });
  return bestIdx;
});

function handleTabChange(value) {
  const item = menuItems.value[value];
  if (item && item.route) {
    emit("tabSelected", item.route);
  }
}
</script>

<style scoped>
.your-stuff-page-header-employee-only {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
  width: 100%;
}

.your-stuff-page-header-employee-only__title-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
}

.your-stuff-page-header-employee-only__title {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #101828;
}

.your-stuff-page-header-employee-only__title--fill {
  flex: 1 0 0;
  min-width: 0;
}

.account-area {
  display: flex;
  flex: 1 0 0;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
  padding: 0 32px 0 8px;
  overflow: hidden;
}

.account-area__identity-group {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.account-area__identity {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
}

.account-area__name,
.account-area__email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.account-area__name {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: #344054;
  margin-bottom: -4px;
}

.account-area__email {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 18px;
  color: #475467;
}

.account-area__logout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
}

.account-area__logout-icon {
  display: block;
}

.account-area__logout:hover {
  background-color: #f2f4f7;
}

.account-area__logout:focus-visible {
  outline: 2px solid #101828;
  outline-offset: 2px;
}

.brand-area {
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 10px;
}

.brand-area__logo {
  display: block;
  flex-shrink: 0;
  width: 189px;
  height: 38px;
}

/*
 * Employee-only tab styling — matches the ticket spec: active tab uses
 * var(--primary, #7f56d9) text and a 2px var(--stroke-selected, #d6bbfb)
 * bottom border; inactive tabs keep var(--descriptions, #475467).
 * HorizontalMenu's defaults are #6941c6 / 3px, so override just those.
 */
:deep(.horizontal-tab) {
  border-bottom: 0.2px solid #b0b0b0;
}

:deep(.horizontal-tab .menu-tabs.v-tab.v-tab--active) {
  color: #7f56d9 !important; /* overrides HorizontalMenu's #6941c6 !important */
}

:deep(.horizontal-tab .v-tabs-slider-wrapper) {
  color: #d6bbfb;
  height: 2px !important; /* overrides HorizontalMenu's 3px slider */
}

/*
 * Count badge: pill, background var(--item-selected, #f9fafb), 1px border
 * var(--stroke-not-selected, #eaecf0), text var(--headings-or-titles, #344054) —
 * one fixed spec for the badge regardless of the tab's active state. HorizontalMenu's
 * default CSS tints the badge purple when its tab is active; override back to spec.
 */
:deep(.horizontal-tab .menu-tabs.v-tab.v-tab--active .count-badge .v-badge__badge) {
  color: #344054 !important;
  background-color: #f9fafb !important;
  border-color: #eaecf0 !important;
}
</style>
